"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, type DayButtonProps } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

// react-day-picker v9 moved the selection-state modifiers (selected, today,
// range start/middle/end, outside, disabled) from the day button onto the
// surrounding grid cell, and no longer puts `aria-selected` on the button.
// The default DayButton doesn't apply any of those modifiers as classes, so
// we re-derive the same look here (selected/today/range pill colors, and
// muted outside/disabled days) directly on the button instead.
function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: DayButtonProps) {
  const ref = React.useRef<HTMLButtonElement>(null);

  // The library focuses the day button imperatively via the `focused`
  // modifier (e.g. after arrow-key navigation or `autoFocus`); the built-in
  // DayButton does this internally, so a custom DayButton must replicate it.
  React.useEffect(() => {
    if (modifiers.focused) {
      ref.current?.focus();
    }
  }, [modifiers.focused]);

  return (
    <button
      ref={ref}
      className={cn(
        // `className` here is `classNames.day_button` from the wrapper below
        // (the button's base look); modifier classes layer on top of it in
        // increasing priority so a day that matches several modifiers (e.g.
        // an outside day that's also the selected range end) ends up with
        // the right combination instead of an arbitrary one.
        className,
        modifiers.disabled && "text-muted-foreground opacity-50",
        modifiers.outside && "text-muted-foreground opacity-50",
        modifiers.today && "bg-accent text-accent-foreground",
        modifiers.selected &&
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        // Middle-of-range days are also `selected`; keep them accent-colored
        // instead of the stronger primary color used for the range ends.
        modifiers.range_middle && "bg-accent text-accent-foreground",
        // A selected day that's outside the current month (e.g. a range
        // extending into the leading/trailing days) stays visibly muted
        // rather than taking on the full primary/accent selected color.
        modifiers.outside &&
          modifiers.selected &&
          "bg-accent/50 text-muted-foreground opacity-30",
      )}
      {...props}
    />
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        // v9 renders the nav once, beside the months rather than inside each
        // caption, so it is positioned across the top of `months` to keep the
        // arrows level with the first and last month captions.
        months:
          "relative flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        month_caption: "flex justify-center pt-1 items-center",
        caption_label: "text-sm font-medium",
        nav: "absolute inset-x-1 top-0 flex h-7 items-center justify-between",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex",
        weekday:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        week: "flex w-full mt-2",
        // The cell (`<td>`) now carries the selection-state modifiers and
        // `data-selected` directly, so it can show the continuous highlight
        // strip across a selected range and round its own row-edge corners,
        // without the old `:has([aria-selected]...)` descendant hacks.
        day: cn(
          "h-9 w-9 text-center text-sm p-0 relative",
          "data-[selected=true]:bg-accent",
          "first:data-[selected=true]:rounded-l-md last:data-[selected=true]:rounded-r-md",
          "focus-within:relative focus-within:z-20",
        ),
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal",
        ),
        // Rounds the cell (not just the button) so the accent background
        // filling the gap around the button's own rounded corners lines up
        // cleanly at the end of a selected range.
        range_end: "rounded-r-md",
        outside: "data-[selected=true]:bg-accent/50",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          if (orientation === "left") {
            return <ChevronLeft className="h-4 w-4" />;
          }
          return <ChevronRight className="h-4 w-4" />;
        },
        DayButton: CalendarDayButton,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
